// Phase 40.2 Plan 06 — PWA splash version gate.
//
// Replaces the PR #295 elapsed-window strategy in App.jsx (AUTO_UPDATE_WINDOW_MS
// = 3000) with a render gate. App.jsx renders a splash overlay until either:
//   (a) onRegisteredSW fires with no waiting worker → bundle is current
//   (b) onNeedRefresh fires while !ready → swap to the new SW before render
//   (c) SW_GATE_TIMEOUT_MS elapses → reveal the cached app (informational
//       needRefresh banner only — no mid-session forced reload)
//
// Composes useRegisterSW from virtual:pwa-register/react. Caller pattern:
//
//   const { ready, updating, needRefresh, updateServiceWorker } = useSWVersionGate();
//   // ...all other hooks declared unconditionally above this line...
//   if (!ready) return <Splash/>;
//   return <NormalReturnTree/>;
//
// All useState/useEffect/useRef calls in this hook are unconditional. The
// render gate is a JSX-level early return at the call site (RESEARCH.md §3.3
// — preserves effect mount order across the App() body).

import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

// Indicator delay before showing the "updating" spinner — keeps fast resolutions
// visually quiet (matches the legacy AUTO_UPDATE_INDICATOR_DELAY_MS at App.jsx:618).
export const AUTO_UPDATE_INDICATOR_DELAY_MS = 500;

// Hard timeout for the splash gate. If neither onRegisteredSW nor onNeedRefresh
// fires within this window, the gate gives up and reveals the cached app — the
// user can still operate, and `needRefresh` becomes informational. 2500ms is
// the upper bound where users perceive "loading" rather than "broken"
// (CONTEXT.md D-04 — locked).
export const SW_GATE_TIMEOUT_MS = 2500;

/**
 * useSWVersionGate — blocks the App render until the service worker state is
 * known or a hard timeout elapses, eliminating the mid-session forced-reload
 * race that Phase 40.1 PR #295 left open.
 *
 * @param {{ timeoutMs?: number }} [opts]
 * @returns {{
 *   ready: boolean,
 *   updating: boolean,
 *   needRefresh: boolean,
 *   updateServiceWorker: (reload?: boolean) => Promise<void>
 * }}
 */
export function useSWVersionGate({ timeoutMs = SW_GATE_TIMEOUT_MS } = {}) {
  const [ready, setReady] = useState(false);
  const [updating, setUpdating] = useState(false);
  const indicatorTimer = useRef(null);
  const readyRef = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(url, r) {
      // Hourly update polling preserved from legacy logic at App.jsx:620.
      if (r) setInterval(() => r.update(), 60 * 60 * 1000);
      // If no waiting worker, the bundle in memory IS current — release the gate.
      if (!r || !r.waiting) {
        readyRef.current = true;
        setReady(true);
      }
      // If r.waiting is non-null, do NOT flip ready here — onNeedRefresh path will.
    },
    onNeedRefresh() {
      // While the splash is up, treat "new version" as "swap to it before render".
      // After the splash hides, do NOT reload mid-session — the banner becomes
      // informational only (Plan 06 / CONTEXT.md D-03). The reload happens on
      // next launch.
      if (!readyRef.current) {
        indicatorTimer.current = setTimeout(
          () => setUpdating(true),
          AUTO_UPDATE_INDICATOR_DELAY_MS
        );
        try {
          updateServiceWorker(true);
        } catch {
          // Fire-and-forget — page reloads. Match the App.jsx:628-633 try/catch
          // shape: if the call throws synchronously, clear the indicator so the
          // splash doesn't strand on "Updating Tend…".
          if (indicatorTimer.current) clearTimeout(indicatorTimer.current);
          setUpdating(false);
        }
      }
      // else: !ready check failed → splash already gone → leave needRefresh as
      // informational. No action — banner copy at App.jsx invites the user to
      // restart on next launch.
    },
  });

  // Hard timeout fallback (path c).
  useEffect(() => {
    const t = setTimeout(() => {
      readyRef.current = true;
      setReady(true);
    }, timeoutMs);
    return () => {
      clearTimeout(t);
      if (indicatorTimer.current) clearTimeout(indicatorTimer.current);
    };
  }, [timeoutMs]);

  return { ready, updating, needRefresh, updateServiceWorker };
}
